import { useState, useEffect, useRef, useCallback } from "react";
import { motion, useInView } from "framer-motion";

// Animation constants
const FADE_IN_UP = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const FADE_IN_UP_DELAYED = (delay) => ({
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, delay }
});

// Statistics constants
const STATS_DATA = [
  { end: 10108, label: "Food Pantry Visits" },
  { end: 1790, label: "Households Served" },
  { end: 1300, label: "Food Bags Picked Up by Social Workers" }
];

// Statistics Counter Component
const Counter = ({ end, duration = 2000, label }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    if (!isInView) return;

    let startTime = null;
    let animationId;
    
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) {
        animationId = requestAnimationFrame(animate);
      }
    };
    animationId = requestAnimationFrame(animate);
    
    return () => {
      if (animationId) cancelAnimationFrame(animationId);
    };
  }, [isInView, end, duration]);

  return (
    <div ref={ref} className="text-center">
      <div 
        className="text-5xl md:text-6xl lg:text-7xl font-black text-[#C41E3A] mb-2"
        style={{ fontFamily: "'Playfair Display', serif" }}
      >
        {count.toLocaleString()}
      </div>
      <div className="text-white text-sm md:text-base font-medium uppercase tracking-wide">
        {label}
      </div>
    </div>
  );
};

// Statistics Section
const StatisticsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section 
      ref={ref}
      data-testid="statistics-section"
      className="py-12 md:py-16 px-4 md:px-8 lg:px-12 bg-[#1A1A1A]"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={FADE_IN_UP.initial}
          animate={isInView ? FADE_IN_UP.animate : {}}
          transition={FADE_IN_UP.transition}
          className="text-center mb-16"
        >
          <h2 
            data-testid="statistics-header"
            className="text-4xl md:text-5xl font-bold text-white mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            In 2025...
          </h2>
          <div className="w-24 h-1 bg-[#C41E3A] mx-auto rounded-full"></div>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-12 md:gap-8">
          {STATS_DATA.map((stat, index) => {
            const delayedAnim = FADE_IN_UP_DELAYED(index * 0.2);
            return (
              <motion.div
                key={stat.label}
                initial={delayedAnim.initial}
                animate={isInView ? delayedAnim.animate : {}}
                transition={delayedAnim.transition}
                data-testid={`stat-${index}`}
                className="bg-[#262626] rounded-2xl p-8 md:p-10"
              >
                <Counter end={stat.end} label={stat.label} />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default StatisticsSection;
